import { Box } from "@mui/material";
import React, { ReactNode } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
import { HeaderText } from "../text/HeaderText.styled";
import { PrimaryButton } from "../buttons/PrimaryButton.styled";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { SecondaryButton } from "../buttons/SecondaryButton.styled";
import EmptyStateCard from "../cards/EmptyStateCard";

/** One of the section's header buttons. */
interface SectionButton {
  icon?: SvgIconComponent;
  text?: string;
  onClick: () => void;
  disabled?: boolean;
}

interface SectionWithButtonsLayoutProps {
  emptyStateText: string;
  header: {
    text: string | ReactNode;
    img?: string;
    icon?: SvgIconComponent;
  };
  primaryButton: SectionButton;
  secondaryButton?: SectionButton;
  children?: ReactNode;
}

const SectionWithButtonsLayout = ({
  emptyStateText,
  header,
  primaryButton,
  secondaryButton,
  children,
}: SectionWithButtonsLayoutProps) => {
  const hasChildren = React.Children.count(children) > 0;
  const PrimaryIcon = primaryButton.icon;
  const SecondaryIcon = secondaryButton?.icon;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {header?.img && (
            <Box component="img" src={header.img} alt="" aria-hidden sx={{ width: "16px", height: "16px" }} />
          )}
          {header?.icon && <header.icon sx={{ fontSize: "18px" }} />}
          <HeaderText>{header.text}</HeaderText>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: "4px" }}>
          {secondaryButton?.onClick && (
            <SecondaryButton
              onClick={secondaryButton.onClick}
              disabled={secondaryButton.disabled}
              startIcon={SecondaryIcon ? <SecondaryIcon /> : <RefreshIcon />}
            >
              {secondaryButton?.text ? secondaryButton.text : "Refresh All"}
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={primaryButton.onClick}
            disabled={primaryButton.disabled}
            startIcon={PrimaryIcon ? <PrimaryIcon /> : <AddIcon />}
          >
            {primaryButton?.text ? primaryButton.text : "Add"}
          </PrimaryButton>
        </Box>
      </Box>
      {hasChildren ? (
        <Box sx={{ px: 0.5, pb: 0.5, gap: 1, display: "flex", flexDirection: "column" }}>{children}</Box>
      ) : (
        <EmptyStateCard text={emptyStateText} />
      )}
    </Box>
  );
};

export default SectionWithButtonsLayout;
